import {ApiService} from "./api.service";


export class ReportService extends ApiService {

  constructor(organizationId) {
    super();
    this.organizationId = organizationId;
    this.endpoint = `/organizations/${organizationId}/reports`;
    this.per = 8;
  }

  // Hits the endpoint that generates a zipped file of any passed report Ids
  // If any reports passed are still processing (http 206), it will return an array of the reports and their statuses
  // ie:
  // [
  //     [42, "COMPLETED"],
  //     [43, "COMPLETED"],
  //     [44, "PROCESSING"]
  // ]

  // Kicks off async zip generation. Returns parent report record for polling.
  async downloadReportsZip(reportIds) {
    try {
      const requestEndpoint = `${this.endpoint}/zipped_reports`;

      return fetch(requestEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({report_ids: reportIds})
      }).then((response) => {
        if (!response.ok) {
          console.warn("error creating zip report", response.status);
          return false;
        }
        return response.json();
      });

    } catch (error) {
      console.error('Download error:', error);
    }
  }

  donorDonationReportPdf(donorId, year) {
    let url = `${this.endpoint}/donor_contribution_statement`;
    const params = {
      "donor_id": donorId,
      "year": year
    };
    return this.post(url, params).then((res) => {
      return res.json;
    });
  }

  donorYearEndSummaryReport(donorId, year) {
    let url = `${this.endpoint}/donor_year_end_summary`;
    const params = {
      "donor_id": donorId,
      "year": year
    };
    return this.post(url, params).then((res) => {
      return res.json;
    });
  }
  householdMemberList(householdId) {
    const url = `${this.endpoint}/create_household_member_list`;
    return this.post(url, { household_id: householdId });
  }

  householdDonationSummary(householdId, year) {
    const url = `${this.endpoint}/create_household_donation_summary`;
    return this.post(url, { household_id: householdId, year: year });
  }
  /**
   * Downloading a file from a link is a bit weird so we wrote this to get around it
   * @param donorIds
   * @returns {Promise<unknown>}
   */
  createDonorList(donorIds) {
    const url = `${this.endpoint}/create_donor_list`;
    return this.post(url, {donor_ids: donorIds});
  }

  createDonationList(params) {
    const url = `${this.endpoint}/create_donation_list`;
    return this.post(url, params);
  }

  createGivingTrendReport(donorIds, period1Start, period1End, period2Start, period2End) {
    const url = `${this.endpoint}/create_giving_trend_report`;
    return this.post(url, {
      donor_ids: donorIds,
      period_1_start: period1Start,
      period_1_end: period1End,
      period_2_start: period2Start,
      period_2_end: period2End
    });
  }

  getReport(id) {
    const url = `${this.endpoint}/${id}`;
    return this.get(url).then((response) => response?.json);
  }

  downloadReportFile(report) {
    const url = `${this.endpoint}/${report.id}/download_report`;
    const currentDate = new Date().toISOString().split('T')[0];

    const filename = `${report.report_type?.toLowerCase() || 'report'}_${currentDate}.xlsx`;

    return this.downloadFile(url, filename);
  }

  downloadReportPdf(reportId, orgName, year) {
    const url = `${this.endpoint}/${reportId}/download_report`;

    const filename = `${orgName}-contribution-report-${year}.pdf`;

    return this.downloadFile(url, filename);
  }

  // TODO these need to be move out of here and into the organization service
  async organizationDonationProjections(organizationId, timeInterval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/donation_report`;

      let params = new URLSearchParams();

      params.set("time_interval", timeInterval);
      let response = await this.get(url, params);

      return response.json;
    } catch {
      return {"error": "error loading donation form"};
    }
  }

  async organizationPaymentProjections(organizationId, timeInterval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/payment_report`;
      let params = new URLSearchParams();

      params.set("time_interval", timeInterval);
      let response = await this.get(url, params);

      return response.json;
    } catch {
      return {"error": "error loading donation form"};
    }
  }

  async organizationTopDonors(organizationId, time_interval = "weekly", count = 3) {
    try {
      let url = `organizations/${organizationId}/top_donors`;
      let params = new URLSearchParams();

      params.set('count', count.toString());
      params.set('time_interval', time_interval);

      let response = await this.get(url, params);

      return response.json;
    } catch {
      return {"error": "error loading donation form"};
    }
  }

  async organizationDonationTypes(organizationId, time_interval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/donation_types`;
      let params = new URLSearchParams();

      params.set('time_interval', time_interval);

      let response = await this.get(url, params);

      return response.json;
    } catch {
      return {"error": "error loading donation form"};
    }
  }

  async donationProcessorTypes(organizationId, time_interval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/donation_processor_types`;
      let params = new URLSearchParams();

      params.set('time_interval', time_interval);

      let response = await this.get(url, params);

      return response.json;
    } catch {
      return {"error": "error loading donation form"};
    }
  }

  async donationsPerFund(organizationId, time_interval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/donations_per_fund`;
      let params = new URLSearchParams();

      params.set('time_interval', time_interval);

      let response = await this.get(url, params);

      return response.json;
    } catch {
      return {"error": "error loading donation form"};
    }
  }

  async donationFunnel(organizationId, time_interval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/donation_funnel`;
      let params = new URLSearchParams();
      params.set('time_interval', time_interval);
      let response = await this.get(url, params);
      return response.json;
    } catch {
      return {"error": "error loading donation funnel"};
    }
  }

  async websiteTraffic(organizationId, time_interval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/website_traffic`;
      let params = new URLSearchParams();
      params.set('time_interval', time_interval);
      let response = await this.get(url, params);
      return response.json;
    } catch {
      return {"error": "error loading website traffic"};
    }
  }

  async donorBehavior(organizationId, time_interval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/donor_behavior`;
      let params = new URLSearchParams();
      params.set('time_interval', time_interval);
      let response = await this.get(url, params);
      return response.json;
    } catch {
      return {"error": "error loading donor behavior"};
    }
  }

  async qrAnalytics(organizationId, time_interval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/qr_analytics`;
      let params = new URLSearchParams();
      params.set('time_interval', time_interval);
      let response = await this.get(url, params);
      return response.json;
    } catch {
      return {"error": "error loading qr analytics"};
    }
  }

  async recentDonations(organizationId) {
    try {
      let url = `/organizations/${organizationId}/recent_donations`;
      let response = await this.get(url);
      return response.json;
    } catch {
      return {"error": "error loading recent donations"};
    }
  }

  async kpiSummary(organizationId, time_interval = "weekly") {
    try {
      let url = `/organizations/${organizationId}/kpi_summary`;
      let params = new URLSearchParams();
      params.set('time_interval', time_interval);
      let response = await this.get(url, params);
      return response.json;
    } catch {
      return {"error": "error loading kpi summary"};
    }
  }

  async calendarData(organizationId, year, month, timeInterval) {
    try {
      let url = `/organizations/${organizationId}/calendar_data`;
      let params = new URLSearchParams();
      params.set('year', year);
      params.set('month', month);
      params.set('time_interval', timeInterval);
      let response = await this.get(url, params);
      return response.json;
    } catch {
      return {"error": "error loading calendar"};
    }
  }

  async show(id) {
    return super.show(id);
  }
}
